import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { Column, DataTableSelectedData } from '@repo/ui/data-table'
import SwitchButtons from '@repo/ui/switch-buttons'
import { useQuery } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import { API } from '../../../api/apis.config'
import { getJourneyPlaces } from '../../../api/journey-place/get-journey-places'
import { getTripGroupingsByTrips } from '../../../api/trip-groupings/get-trip-groupings-by-trips'
import { getTrips } from '../../../api/trips/get-trips'
import { AccomodationCategoryEnum } from '../../../lib/types/enums/product/accomodation-category.enum'
import { TripTypeEnum } from '../../../lib/types/enums/trip/trip-type.enum'
import { CountryLight } from '../../../lib/types/models/country/country'
import { JourneyPlaceLight } from '../../../lib/types/models/journey-place/journey-place'
import { JourneyTripOptionInput } from '../../../lib/types/models/product/journey-input'
import { TripProperties } from '../../../lib/types/models/trip/properties.trip'
import { TripFilters, TripLight } from '../../../lib/types/models/trip/trip'
import { TripGrouping } from '../../../lib/types/models/trip-grouping/trip-grouping'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { idOnlySchema } from '../../../lib/utils/validations/id-only.validation'
import { createNumberSchema } from '../../../lib/utils/validations/int-array-validation'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import { getTripGroupingColumns } from '../../../views/trip-groupings/utils'
import { getTripColumns } from '../../../views/trips/utils'
import DynamicDataTable, { DynamicTableRef } from '../../elements/dynamic-table'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormLabelContainer
} from '../../elements/forms/form'
import ControlledSelectInput from '../../elements/inputs/controlled-select-input'
import SearchableDataTable from '../../elements/searchable-data-table'
import ValidationButtons from '../../elements/validation-buttons'

type Props = {
  className?: string
  country: CountryLight
  accomodationCategory: AccomodationCategoryEnum
  defaultValues?: Partial<JourneyTripOptionInput>
  isLoading?: boolean
  handleSubmit: (values: JourneyTripOptionInput) => void
  onCancel: () => void
}

const formSchema: ZodType<JourneyTripOptionInput> = z.object({
  place: idOnlySchema<JourneyPlaceLight>(createNumberSchema('id', { isInteger: true }), {
    message: 'Les données du lieu de séjour sont invalides'
  }),
  mainTrip: idOnlySchema<TripLight>(createNumberSchema('id', { isInteger: true }), {
    message: 'Les données du voyage principal sont invalides'
  }),
  secondaryTrips: z.array(
    idOnlySchema<TripLight>(createNumberSchema('id', { isInteger: true }), {
      message: 'Les données des voyages secondaires sont invalides'
    })
  )
})

type SwitchButtonsType = 'trips' | 'trip-groupings'

const JourneyTripOptionForm = ({
  className,
  defaultValues,
  isLoading,
  country,
  accomodationCategory,
  handleSubmit,
  onCancel
}: Props) => {
  const [value, setValue] = useState<SwitchButtonsType>('trips')
  const [selectedTripGroupings, setSelectedTripGroupings] = useState<TripGrouping[]>([])
  const [secondaryTripsSearch, setSecondaryTripsSearch] = useState('')
  const secondaryTripsTableRef = useRef<DynamicTableRef<TripLight>>(null)

  const form = useForm<JourneyTripOptionInput>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: FORM_MODE
  })

  const secondaryTrips = form.watch('secondaryTrips')

  useQuery({
    queryKey: ['trip-groupings-by-trips', secondaryTrips.map((trip) => trip.id)],
    queryFn: async () => {
      const res = await getTripGroupingsByTrips(secondaryTrips.map((trip) => trip.id))
      setSelectedTripGroupings(res)
      return res
    }
  })

  const { data: trips } = useQuery({
    queryKey: ['trips', { filter: { countryCodes: [country.code] } }],
    queryFn: () => getTrips({ filter: { countryCodes: [country.code] } })
  })

  const { data: journeyPlaces } = useQuery({
    queryKey: ['journeyPlaces', { filter: { countryIds: [country.id], accomodationCategory } }],
    queryFn: () => getJourneyPlaces({ filter: { countryIds: [country.id], accomodationCategory } })
  })
  const setSecondaryTrips = (trips: TripLight[]) => {
    // add new trips to the secondary trips and remove the duplicates
    const uniqueTrips = trips.filter(
      (trip, index, self) => index === self.findIndex((t) => t.id === trip.id)
    )
    form.setValue('secondaryTrips', uniqueTrips)
  }

  const secondaryTripFilters: TripFilters = {
    countryCodes: [country.code],
    type: TripTypeEnum.SECONDARY
  }

  const handleSecondaryTripsSelectionChange = async (
    selectedData: DataTableSelectedData<TripLight>
  ) => {
    if (!selectedData.isAllSelected) {
      setSecondaryTrips(selectedData.rows)
      return
    }

    secondaryTripsTableRef.current?.startSelectAllLoading()
    try {
      const res = await getTrips({
        search: secondaryTripsSearch,
        filter: secondaryTripFilters,
        version: 'LIGHT'
      })

      setSecondaryTrips(res.data)
    } finally {
      secondaryTripsTableRef.current?.endSelectAllLoading()
    }
  }

  const getSelectableSecondaryTrips = (trips: TripGrouping['trips']) =>
    trips.filter(
      (trip) =>
        trip.type === TripTypeEnum.SECONDARY &&
        trip.countries?.some((tripCountry) => tripCountry.code === country.code)
    )

  const adaptSecondaryTrips = (prev: TripGrouping[], selectedRows: TripGrouping[]) => {
    // Check for the unchecked trip groupings and remove their trips from the secondary trips
    // Check for the checked trip groupings and add their trips to the secondary trips
    const checkedTripGroupings = selectedRows.filter(
      (selectedRow) => !prev.find((prevRow) => prevRow.id === selectedRow.id)
    )
    const uncheckedTripGroupings = prev.filter(
      (prevRow) => !selectedRows.find((selectedRow) => selectedRow.id === prevRow.id)
    )

    const newTrips = secondaryTrips.filter(
      (trip) =>
        !uncheckedTripGroupings.find((grouping) => grouping.trips.some((t) => t.id === trip.id))
    )

    const uniqueTrips = newTrips.concat(
      checkedTripGroupings.flatMap((grouping) => getSelectableSecondaryTrips(grouping.trips))
    )

    setSecondaryTrips(uniqueTrips)
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <div className={cn('flex flex-col gap-5', className)}>
          <FormField
            control={form.control}
            name="place"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <FormLabelContainer>
                    <FormLabel>Choisir un lieu de séjour</FormLabel>
                    <ControlledSelectInput
                      required
                      {...field}
                      label={'Lieu'}
                      searchable
                      options={journeyPlaces?.data?.map((place) => ({
                        label: place.name,
                        value: place.id
                      }))}
                      selectedOption={
                        journeyPlaces?.data?.find((place) => place.id === field.value?.id) && {
                          label: field.value.name,
                          value: field.value.id
                        }
                      }
                      dropDownPosition="above"
                      onChange={(value) => {
                        const place = journeyPlaces?.data?.find((place) => place.id === value)
                        if (place) {
                          field.onChange({
                            id: place.id,
                            name: place.name
                          })
                        }
                      }}
                    />
                  </FormLabelContainer>
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="mainTrip"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <FormLabelContainer>
                    <FormLabel>Choisir un voyage principal</FormLabel>
                    <ControlledSelectInput
                      required
                      {...field}
                      label={'Voyage principal'}
                      searchable
                      options={trips?.data
                        ?.filter((trip) => trip.type === TripTypeEnum.PRIMARY)
                        .map((trip) => ({
                          label: TripProperties.getTripLabel(trip),
                          value: trip.id
                        }))}
                      dropDownPosition="above"
                      selectedOption={
                        trips?.data?.find((trip) => trip.id === field.value?.id) && {
                          label: TripProperties.getTripLabel(field.value),
                          value: field.value.id
                        }
                      }
                      onChange={(value) => {
                        const trip = trips?.data?.find((trip) => trip.id === value)
                        if (trip) {
                          field.onChange({
                            id: trip.id,
                            type: trip.type,
                            departure: trip.departure,
                            arrival: trip.arrival,
                            journeyPlace: trip.journeyPlace,
                            hasTransport: trip.hasTransport,
                            transportMeans: trip.transportMeans
                          })

                          if (!trip.hasTransport) form.setValue('secondaryTrips', [])
                        }
                      }}
                    />
                  </FormLabelContainer>
                </FormControl>
              </FormItem>
            )}
          />
          <div
            className={cn({
              hidden: !form.watch('mainTrip')?.hasTransport
            })}
          >
            <SwitchButtons<SwitchButtonsType>
              items={[
                { label: 'Voyages', value: 'trips' },
                { label: 'Groupes de voyage', value: 'trip-groupings' }
              ]}
              value={value}
              onChange={setValue}
            />

            <div className="h-4" />

            {value === 'trips' && (
              <DynamicDataTable<TripLight, TripFilters>
                ref={secondaryTripsTableRef}
                columns={getTripColumns('SUMMARY') as Column<TripLight>[]}
                description={`Choisir un ou plusieurs voyages secondaires`}
                endpoint={API.trips.getAll.url}
                filters={secondaryTripFilters}
                headerType="light"
                permissions={{ delete: false }}
                selectable
                selectedData={{ expandedRows: [], isAllSelected: false, rows: secondaryTrips }}
                onSearchChange={setSecondaryTripsSearch}
                onSelectedDataChange={handleSecondaryTripsSelectionChange}
              />
            )}

            {value === 'trip-groupings' && (
              <SearchableDataTable<TripGrouping, null>
                columns={getTripGroupingColumns('DENSE')}
                selectedRows={selectedTripGroupings}
                onChange={(selectedRows) => {
                  setSelectedTripGroupings((prev) => {
                    adaptSecondaryTrips(prev, selectedRows)
                    return selectedRows
                  })
                }}
                disableSelectAll
                description={`Vous pouvez choisir plus d'un groupe de voyages`}
                endpoint={API.tripGroupings.getAll.url}
                confirmationText="Valider"
                isLoading={isLoading}
                onCancel={onCancel}
              />
            )}
          </div>
        </div>

        <ValidationButtons
          hasBorderTop
          buttonsConfig={modalButtonConfig({ onCancel, onConfirm: handleSubmit })}
        />
      </form>
    </Form>
  )
}

export default JourneyTripOptionForm
